import { PokalSpecification } from "pokal_api"
import {
    ActionReducerMapBuilder,
    AsyncThunk,
    createAsyncThunk,
    Draft,
    PayloadAction,
    SerializedError
} from "@reduxjs/toolkit"
import { AsyncThunkConfig } from "@reduxjs/toolkit/dist/createAsyncThunk"
import { AppDispatch } from "../store/store"

function specificationToUniqueName<INPUT, OUTPUT>(
    specification: PokalSpecification<INPUT, OUTPUT>
): string {
    return specification.getLocation().path + specification.getLocation().method
}

export function thunkifySpecification<INPUT, OUTPUT>(
    specification: PokalSpecification<INPUT, OUTPUT>
): AsyncThunk<OUTPUT, INPUT, AsyncThunkConfig> {
    return createAsyncThunk(
        specificationToUniqueName(specification),
        async (args: INPUT) => {
            return await specification.request(args)
        }
    )
}

export function dispatchThunk<INPUT, OUTPUT>(
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    dispatch: AppDispatch,
    specification: PokalSpecification<INPUT, OUTPUT>,
    args: INPUT,
    handler?: {
        do?: (out: OUTPUT) => void
        catch?: (e: any) => void
        finally?: () => void
    }
) {
    const thunk = thunkifySpecification(specification)
    dispatch(thunk(args))
        .unwrap()
        .then((output: OUTPUT) => {
            if (handler && handler.do) handler.do(output)
            if (handler && handler.finally) handler.finally()
        })
        .catch((e: any) => {
            if (handler && handler.catch) handler.catch(e)
            if (handler && handler.finally) handler.finally()
        })
}

export function thunkify<INPUT, OUTPUT, STATE>(
    builder: ActionReducerMapBuilder<STATE>,
    specification: PokalSpecification<INPUT, OUTPUT>,
    handler: (
        state: Draft<STATE>,
        action: PayloadAction<
            OUTPUT,
            string,
            { arg: INPUT; requestId: string; requestStatus: "fulfilled" },
            never
        >
    ) => void
) {
    builder.addCase(thunkifySpecification(specification).fulfilled, handler)
}

export function thunkicatch<INPUT, OUTPUT, STATE>(
    builder: ActionReducerMapBuilder<STATE>,
    specification: PokalSpecification<INPUT, OUTPUT>,
    handler: (
        state: Draft<STATE>,
        action: PayloadAction<
            unknown,
            string,
            {
                arg: INPUT
                requestId: string
                requestStatus: "rejected"
                aborted: boolean
                condition: boolean
            } & (
                | { rejectedWithValue: true }
                | ({ rejectedWithValue: false } & {})
            ),
            SerializedError
        >
    ) => void
) {
    builder.addCase(thunkifySpecification(specification).rejected, handler)
}
